/**
 * Money on Home (brands that sell): members now, gained and lost, and rentals and purchases with
 * their estimate at store prices, against the period before; the way to Analytics › Money. Four
 * figures in the card, two by two (no tiles inside a card).
 */

import Button from "@web-ui/components/Button";
import Skeleton from "@web-ui/components/Skeleton";
import type { MoneyReport } from "../api";
import { MetricFigure } from "../components/metric-figure";
import { definition } from "../definitions";
import { change, count, estimatedTotal } from "../format";
import type { Report } from "../use-report";
import type { Pack } from "@/vocabulary/vocabulary";

const soldOf = (m: MoneyReport) => m.sales.filter((s) => s.kind !== "subscription");

export function MoneySummary({ report, pack, moneyHref }: { report: Report<{ now: MoneyReport; before: MoneyReport } | null>; pack: Pack; moneyHref: string }) {
  const m = report.data?.now;
  const b = report.data?.before;
  const sold = m ? soldOf(m).reduce((n, s) => n + s.count, 0) : 0;
  const estimate = m ? estimatedTotal(soldOf(m)) : null;
  return (
    <section className={`card flex flex-col gap-3 transition-opacity ${report.stale ? "opacity-60" : ""}`} aria-labelledby="money" aria-busy={(!m && !report.failed) || report.stale || undefined}>
      <div className="flex items-center gap-3">
        <div className="me-auto min-w-0 ps-card-inset py-1">
          <h2 id="money" className="text-sub-title-1">Money</h2>
          <p className="text-body-2 text-t-secondary">Estimated, at store prices before store fees.</p>
        </div>
        <Button as="a" variant="ghost" size="sm" href={moneyHref}>See money</Button>
      </div>
      {!m ? (report.failed ? <p className="text-body-2 text-t-secondary ps-card-inset pb-2">Not available right now.</p> : <div className="px-card-inset pb-2"><Skeleton lines={6} /></div>) : (
        <div className="grid grid-cols-2 gap-x-6 gap-y-6 px-card-inset py-2">
          <MetricFigure definition={definition("subscribers", pack)} value={count(m.subscribers.active)} />
          <MetricFigure definition={definition("started", pack)} value={count(m.subscribers.started)} change={change(m.subscribers.started, b?.subscribers.started)} />
          <MetricFigure definition={definition("ended", pack)} value={count(m.subscribers.ended)} change={change(m.subscribers.ended, b?.subscribers.ended)} upIsGood={false} />
          <MetricFigure definition={definition("sales", pack)} value={sold ? `${count(sold)}${estimate ? ` · ${estimate}` : ""}` : "0"} change={change(sold, b ? soldOf(b).reduce((n, s) => n + s.count, 0) : null)} />
        </div>
      )}
    </section>
  );
}
